import React, { useEffect, useMemo, useState } from 'react'
import { Accordion, Form, Input } from 'semantic-ui-react'
import styled from 'styled-components'

import ScoreIconDropdown from 'components/ScoreIconDropdown'
import constants from 'config/constants'
import ColorInput from 'components/ColorInput'
import FeedbackWidgetIconDropdown from 'components/FeedbackWidgetIconDropdown'

type WidgetFormProps = {
  styles?: object
  settings?: object
  fontStyle: string
  onChange: Function
  additionalAccordions?: {
    title: {
      children: React.ReactElement | string
    }
    content: {
      children: React.ReactElement | string
    }
  }[]
  surveyOptions?: object[]
  widgetType: string
  setHasWidgetFormUnsavedChange: Function
}

export default function WidgetForm({
  styles = {},
  settings = {},
  fontStyle,
  onChange,
  additionalAccordions = [],
  widgetType,
  surveyOptions = [],
  setHasWidgetFormUnsavedChange,
}: WidgetFormProps) {
  const groups = useMemo(
    () => createGroups(fontStyle, styles, settings),
    [fontStyle, settings, styles]
  )
  const [fontStyleState, setFontStyleState] = useState(fontStyle)
  const [stylesState, setStylesState] = useState(styles)
  const [settingsState, setSettingsState] = useState(settings)

  // reset state when new value from API are pass through these props
  useEffect(() => {
    setFontStyleState(fontStyle)
    setStylesState(styles)
    setSettingsState(settings)
  }, [fontStyle, settings, styles])

  useEffect(() => {
    onChange({
      fontStyle: fontStyleState,
      styles: stylesState,
      settings: settingsState,
    })
  }, [fontStyleState, onChange, settingsState, stylesState])

  function handleFieldChange(field: FieldType, value: string | number) {
    setHasWidgetFormUnsavedChange(true)

    if (field.name === 'font') {
      return setFontStyleState(value as unknown as string)
    }

    const isStyle = field.property.startsWith('--')
    if (isStyle) {
      return setStylesState((stylesState) => {
        return {
          ...stylesState,
          [field.property]: value,
        }
      })
    }

    // is settings
    return setSettingsState((settingsState) => {
      return {
        ...settingsState,
        [field.property]: value,
      }
    })
  }

  return (
    <Accordion
      styled
      fluid
      defaultActiveIndex={[0]}
      exclusive={false}
      panels={[
        ...groups.map((group: Group) => {
          return {
            key: group.name,
            title: {
              children: <span className="first-letter:capitalize inline-block">{group.label}</span>,
            },
            content: {
              children: (
                <Form>
                  <Grid className="grid gap-x-4">
                    {group.fields.map((field: FieldType) => {
                      const value =
                        field.name === 'font'
                          ? fontStyleState
                          : stylesState[field.property] || settingsState[field.property]
                      return (
                        <Form.Field key={field.property}>
                          <label className="first-letter:!capitalize">{field.label}</label>
                          <Field
                            field={field}
                            value={value}
                            onChange={(value) => handleFieldChange(field, value)}
                            surveyOptions={surveyOptions}
                            widgetType={widgetType}
                            iconColor={getIconColor(widgetType, stylesState)}
                          />
                        </Form.Field>
                      )
                    })}
                  </Grid>
                </Form>
              ),
            },
          }
        }),
        ...additionalAccordions,
      ]}
    />
  )
}

const Grid = styled.div`
  --min-width: 200px;
  --number-of-columns: 4;
  --gap-x: 16px;
  --break-point: var(--number-of-columns) * var(--min-width) + (var(--number-of-columns) - 1) *
    var(--gap-x);
  grid-template-columns: repeat(
    auto-fit,
    /* https://www.bram.us/2019/09/24/flexbox-albatross-with-grids/ */
      minmax(
        min(calc((var(--break-point) - 100%) * 999), 100%),
        /* max width calculated based on number of columns and gap size */
          calc(100% / var(--number-of-columns) - var(--gap-x) * (1 - 1 / var(--number-of-columns)))
      )
  );

  > * {
    /* Set max width when fields stack */
    max-width: 400px;
  }
`

type Group = {
  name: string
  label: string
  fields: FieldType[]
  order: number
}

type FieldType = {
  property: string
  name: string
  label: string
  value: Value
  order: number
}

type Value = string | number

function createGroups(fontStyle: string, styles: object, settings: object): Group[] {
  const fieldsByGroup: { [group: string]: Group } = {}
  Object.entries({
    ...styles,
    ...settings,
    // hard coded the order according to this table
    // https://docs.google.com/spreadsheets/d/1x5M6GdVkuxwvZzi0pJ0dm0o--HaLdimIntygZc9eUg0/edit#gid=1019781814
    // Basically, we want the font to be in the global group, but could be in any order.
    '--0-global-20-font': fontStyle,
  }).forEach(([property, value]) => {
    if (isValidProperty(property)) {
      const groupName = extractGroup(property)
      if (!fieldsByGroup[groupName]) {
        fieldsByGroup[groupName] = {
          name: groupName,
          label: groupName.split('-').join(' '),
          order: extractGroupOrder(property),
          fields: [],
        }
      }

      const fieldName = extractField(property)
      fieldsByGroup[groupName].fields.push({
        name: fieldName,
        label: fieldName.split('-').join(' '),
        property,
        value,
        order: extractFieldOrder(property),
      })
    }
  })

  const groups = Object.values(fieldsByGroup).reduce((groups: Group[], group) => {
    group.fields.sort((fieldA, fieldB) => {
      return fieldA.order - fieldB.order
    })
    groups.push(group)
    return groups
  }, [])

  groups.sort((groupA, groupB) => {
    return groupA.order - groupB.order
  })

  return groups
}

function isValidProperty(property) {
  return Boolean(PROPERTY_REGEX.exec(property))
}

const PROPERTY_REGEX =
  /(?:--)?(?<groupOrder>\d)-(?<groupName>[\w-]+)-(?<fieldOrder>\d{2})-(?<fieldName>[\w-]+)/

/**
 *
 * @param property e.g. `--0-global-15-text-align` or `0-global-18-date-range`
 */
function extractGroup(property: string): string {
  return PROPERTY_REGEX.exec(property)?.groups?.groupName as string
}

/**
 *
 * @param property e.g. `--0-global-15-text-align`
 */
function extractField(property: string): string {
  return PROPERTY_REGEX.exec(property)?.groups?.fieldName as string
}

/**
 *
 * @param property e.g. `--0-global-15-text-align`
 */
function extractGroupOrder(property: string): number {
  return Number(PROPERTY_REGEX.exec(property)?.groups?.groupOrder as string)
}

/**
 *
 * @param property e.g. `--0-global-15-text-align`
 */
function extractFieldOrder(property: string): number {
  return Number(PROPERTY_REGEX.exec(property)?.groups?.fieldOrder as string)
}

type FieldProps = {
  field: FieldType
  value: string
  onChange: Function
  surveyOptions?: object[]
  widgetType: string
  iconColor: string
}

function Field({
  field,
  value: valueState,
  onChange,
  surveyOptions,
  widgetType,
  iconColor,
}: FieldProps) {
  const defaultValue = field.value
  const value = valueState || defaultValue
  if (field.name === 'text-align') {
    return (
      <DropdownField
        data-property={field.property}
        value={value}
        options={constants.TEXT_ALIGNMENT_OPTIONS}
        onChange={onChange}
      />
    )
  }
  if (field.name === 'font') {
    return (
      <DropdownField
        data-property={field.property}
        value={value}
        options={constants.WIDGET_FONT_OPTIONS}
        onChange={onChange}
      />
    )
  }
  if (field.name === 'shadow') {
    return (
      <DropdownField
        data-property={field.property}
        value={value}
        options={constants.SHADOW_OPTIONS}
        onChange={onChange}
      />
    )
  }
  if (field.name === 'company-visibility') {
    return (
      <DropdownField
        data-property={field.property}
        value={value}
        options={constants.COMPANY_VISIBILITY_OPTIONS}
        onChange={onChange}
      />
    )
  }
  if (field.name === 'customer-name-format') {
    return (
      <DropdownField
        data-property={field.property}
        value={value}
        options={constants.CUSTOMER_NAME_FORMAT_OPTIONS}
        onChange={onChange}
      />
    )
  }
  if (field.name.includes('font-weight')) {
    return (
      <DropdownField
        data-property={field.property}
        value={value}
        options={constants.FONT_WEIGHT_OPTIONS}
        onChange={onChange}
      />
    )
  }
  if (field.name.includes('font-style')) {
    return (
      <DropdownField
        data-property={field.property}
        value={value}
        options={constants.FONT_STYLE_OPTIONS}
        onChange={onChange}
      />
    )
  }
  if (field.name === 'date-format') {
    return (
      <DropdownField
        data-property={field.property}
        value={value}
        options={constants.DATE_FORMAT_OPTIONS}
        onChange={onChange}
      />
    )
  }
  if (field.name === 'date-range') {
    return (
      <DropdownField
        data-property={field.property}
        value={value}
        options={constants.DATE_RANGE_OPTIONS}
        onChange={onChange}
      />
    )
  }
  if (field.name === 'icon' && widgetType === 'score') {
    return (
      <ScoreIconDropdown
        data-property={field.property}
        defaultValue={field.value}
        onChange={onChange}
      />
    )
  }
  if (field.name === 'icon') {
    return (
      <FeedbackWidgetIconDropdown
        data-property={field.property}
        defaultValue={field.value}
        onChange={onChange}
        iconColor={iconColor}
      />
    )
  }
  if (field.name.includes('color')) {
    return (
      <ColorField
        data-property={field.property}
        fieldName={field.name}
        color={value}
        onColorChange={onChange}
      />
    )
  }
  if (field.name === 'survey') {
    return (
      <DropdownField
        data-property={field.property}
        value={value}
        options={surveyOptions}
        onChange={onChange}
      />
    )
  }

  const fieldType = getFieldType(field.name, value)
  return (
    <Input
      data-property={field.property}
      type={fieldType}
      value={value}
      onChange={(_, { value }) => {
        onChange(value)
      }}
      onBlur={({ currentTarget }) => {
        const isNumberValueMissing = !currentTarget?.value && fieldType === 'number'
        if (isNumberValueMissing) {
          const defaultValue = 0
          onChange(defaultValue)
        }
      }}
    />
  )
}

function ColorField({ 'data-property': dataProperty, fieldName, color, onColorChange }) {
  const normalizedColorFieldName = normalizeColorFieldName(fieldName)
  const optionsMap = {
    'background-color': [
      'transparent',
      '#FFFFFF',
      '#D3F8DF',
      '#FFEFD5',
      '#E6F2FF',
      '#EDE4FF',
      '#FFDDE1',
      '#F4F5F7',
      '#091E42',
      '#42526E',
    ],
    'border-color': [
      'transparent',
      '#FFFFFF',
      '#091E42',
      '#42526E',
      '#4E4E4E',
      '#9B9B9B',
      '#DFE1E6',
      '#4EC677',
      '#FFC400',
      '#00B8D9',
    ],
    'font-color': [
      '#091E42',
      '#172B4D',
      '#42526E',
      '#97A0AF',
      '#373F46',
      '#4E4E4E',
      '#9B9B9B',
      '#DFE1E6',
      '#F4F5F7',
      '#FFFFFF',
    ],
    'icon-color': [
      '#4EC677',
      '#FFC400',
      '#FFDE62',
      '#FF7C5F',
      '#5F5FFC',
      '#00B8D9',
      '#53B3FF',
      '#8446AB',
      '#3FCAB7',
      '#FF556D',
    ],
  }
  return (
    <ColorInput
      data-property={dataProperty}
      colorOptions={optionsMap[normalizedColorFieldName]}
      color={color}
      onColorChange={onColorChange}
    />
  )
}

function normalizeColorFieldName(fieldName: string): string {
  if (fieldName.endsWith('background-color')) {
    return 'background-color'
  }

  if (fieldName.endsWith('border-color')) {
    return 'border-color'
  }

  if (fieldName.endsWith('font-color')) {
    return 'font-color'
  }

  if (fieldName.endsWith('icon-color')) {
    return 'icon-color'
  }

  // The code should never reach this point, so I'm casting the type to satisfy TypeScript here.
  return undefined as unknown as string
}

function getFieldType(name, value) {
  if (name === 'description') {
    return 'text'
  }

  const isNumber = (number) => !isNaN(number)
  if (isNumber(value)) {
    return 'number'
  }

  return 'text'
}

function DropdownField({ 'data-property': dataProperty, value, options, onChange }) {
  return (
    <Form.Select
      data-property={dataProperty}
      fluid
      value={value}
      options={options}
      onChange={(_, { value }) => onChange(value)}
      search
    />
  )
}

function getIconColor(widgetType, stylesState) {
  if (widgetType === 'score') {
    return null
  } else if (widgetType === 'popup_box') {
    return stylesState['--1-header-15-icon-color']
  } else {
    //carousel | grid | masonry
    return stylesState['--1-header-05-icon-color']
  }
}



// WEBPACK FOOTER //
// ./src/components/WidgetForm/index.tsx